import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { EvaluationFilters } from '../types';
import {
  Search,
  Filter,
  RotateCcw,
  Calendar,
  Heart,
  BookMarked,
  Zap,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface EvaluationFiltersProps {
  filters: EvaluationFilters;
  onFiltersChange: (filters: EvaluationFilters) => void;
  availableModels?: string[];
}

export function EvaluationFiltersComponent({
  filters,
  onFiltersChange,
  availableModels = [],
}: EvaluationFiltersProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Helper para formatear números sin decimales innecesarios
  const formatNumber = (num: number) => {
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  };

  const handleFilterChange = (key: keyof EvaluationFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = Object.keys(filters).some(key => {
    const value = filters[key as keyof EvaluationFilters];
    if (value === undefined || value === null || value === '') return false;
    // Para arrays (como scoreRange), verificar si tiene valores diferentes a los default
    if (Array.isArray(value)) return !(value[0] === 0 && value[1] === 10);
    return true;
  });

  const activeFilterCount = Object.keys(filters).filter(key => {
    const value = filters[key as keyof EvaluationFilters];
    if (value === undefined || value === null || value === '') return false;
    // Para arrays (como scoreRange), verificar si tiene valores diferentes a los default
    if (Array.isArray(value)) return !(value[0] === 0 && value[1] === 10);
    return true;
  }).length;

  return (
    <Card className='border-0 shadow-sm mb-6'>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <Filter className='h-5 w-5 text-blue-600' />
            <CardTitle className='text-lg'>Filtros Avanzados</CardTitle>
            {activeFilterCount > 0 && (
              <Badge className='bg-blue-100 text-blue-800'>
                {activeFilterCount} activo{activeFilterCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {hasActiveFilters && (
              <Button
                variant='outline'
                size='sm'
                onClick={clearFilters}
                className='text-gray-600 hover:text-gray-900'
              >
                <RotateCcw className='h-4 w-4 mr-1' />
                Limpiar
              </Button>
            )}
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsExpanded(!isExpanded)}
              className='text-gray-600'
            >
              {isExpanded ? (
                <ChevronUp className='h-4 w-4' />
              ) : (
                <ChevronDown className='h-4 w-4' />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {(isExpanded || hasActiveFilters) && (
        <CardContent className='space-y-6'>
          {/* Búsqueda de texto */}
          <div className='grid grid-cols-1 gap-4'>
            <div>
              <Label className='text-sm font-semibold text-gray-700 flex items-center gap-1'>
                <Search className='h-4 w-4' />
                Búsqueda en Query/Respuesta
              </Label>
              <Input
                placeholder='Buscar en consultas y respuestas...'
                value={filters.search || ''}
                onChange={e => handleFilterChange('search', e.target.value)}
                className='mt-1'
              />
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
            {/* Estados especiales */}
            <div>
              <Label className='text-sm font-semibold text-gray-700 mb-3 block'>
                Estados Especiales
              </Label>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Button
                    variant={filters.is_liked ? 'default' : 'outline'}
                    size='sm'
                    onClick={() =>
                      handleFilterChange(
                        'is_liked',
                        filters.is_liked ? undefined : true
                      )
                    }
                    className={`${
                      filters.is_liked
                        ? 'bg-pink-600 hover:bg-pink-700'
                        : 'text-pink-600 border-pink-300 hover:bg-pink-50'
                    }`}
                  >
                    <Heart
                      className={`h-4 w-4 mr-1 ${filters.is_liked ? 'fill-current' : ''}`}
                    />
                    Liked
                  </Button>
                </div>
                <div className='flex items-center gap-2'>
                  <Button
                    variant={filters.is_training_data ? 'default' : 'outline'}
                    size='sm'
                    onClick={() =>
                      handleFilterChange(
                        'is_training_data',
                        filters.is_training_data ? undefined : true
                      )
                    }
                    className={`${
                      filters.is_training_data
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : 'text-purple-600 border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <BookMarked
                      className={`h-4 w-4 mr-1 ${filters.is_training_data ? 'fill-current' : ''}`}
                    />
                    Training Data
                  </Button>
                </div>
                <div className='flex items-center gap-2'>
                  <Button
                    variant={filters.is_auto ? 'default' : 'outline'}
                    size='sm'
                    onClick={() =>
                      handleFilterChange(
                        'is_auto',
                        filters.is_auto ? undefined : true
                      )
                    }
                    className={`${
                      filters.is_auto
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'text-emerald-600 border-emerald-300 hover:bg-emerald-50'
                    }`}
                  >
                    <Zap className={`h-4 w-4 mr-1`} />
                    Automáticas (≥8)
                  </Button>
                </div>
              </div>
            </div>

            {/* Rango de puntuación */}
            <div>
              <Label className='text-sm font-semibold text-gray-700 flex items-center gap-1 mb-3'>
                <BarChart3 className='h-4 w-4' />
                Rango de Puntuación
              </Label>
              <div className='space-y-3'>
                <Slider
                  value={filters.scoreRange || [0, 10]}
                  onValueChange={value =>
                    handleFilterChange('scoreRange', value)
                  }
                  min={0}
                  max={10}
                  step={0.1}
                  className='mt-4'
                />
                <div className='flex justify-between text-xs text-gray-600'>
                  <span>{formatNumber(filters.scoreRange?.[0] || 0)}</span>
                  <span>{formatNumber(filters.scoreRange?.[1] || 10)}</span>
                </div>
              </div>
            </div>

            {/* Modelo y Estado */}
            <div className='space-y-4'>
              {/* Modelo */}
              <div>
                <Label className='text-sm font-semibold text-gray-700'>
                  Modelo
                </Label>
                <Select
                  value={filters.model || 'all'}
                  onValueChange={value =>
                    handleFilterChange(
                      'model',
                      value === 'all' ? undefined : value
                    )
                  }
                >
                  <SelectTrigger className='mt-1'>
                    <SelectValue placeholder='Todos los modelos' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>Todos los modelos</SelectItem>
                    {availableModels.map(model => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Estado */}
              <div>
                <Label className='text-sm font-semibold text-gray-700'>
                  Estado
                </Label>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={value =>
                    handleFilterChange(
                      'status',
                      value === 'all' ? undefined : value
                    )
                  }
                >
                  <SelectTrigger className='mt-1'>
                    <SelectValue placeholder='Todos los estados' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>Todos los estados</SelectItem>
                    <SelectItem value='completed'>Completado</SelectItem>
                    <SelectItem value='failed'>Fallido</SelectItem>
                    <SelectItem value='pending'>Pendiente</SelectItem>
                    <SelectItem value='in-progress'>En Progreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Filtros de fecha */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t'>
            <div>
              <Label className='text-sm font-semibold text-gray-700 flex items-center gap-1'>
                <Calendar className='h-4 w-4' />
                Fecha Desde
              </Label>
              <Input
                type='date'
                value={filters.dateFrom || ''}
                onChange={e => handleFilterChange('dateFrom', e.target.value)}
                className='mt-1'
              />
            </div>
            <div>
              <Label className='text-sm font-semibold text-gray-700 flex items-center gap-1'>
                <Calendar className='h-4 w-4' />
                Fecha Hasta
              </Label>
              <Input
                type='date'
                value={filters.dateTo || ''}
                onChange={e => handleFilterChange('dateTo', e.target.value)}
                className='mt-1'
              />
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
